import type { FC, SyntheticEvent } from 'react'
import { useCallback, useRef } from 'react'

import { AGENT_FALLBACK_THUMBNAIL } from '@liveHelper/constants'
import { useAppointment } from '@liveHelper/context/AppointmentContext/AppointmentContext'
import {
  actionsSelector,
  paramsSelector,
  previousConversationSelector,
  useStore,
} from '@liveHelper/models/store'
import {
  useTrackSubmitCustomerSurveyFeedback,
  useTrackSubmitCustomerSurveyRating,
} from '@liveHelper/tracking/useLiveHelperTracking'
import { CustomerSurvey as StatelessCustomerSurvey } from '@oneToOne/shared/components/CustomerSurvey'
import { useDisconnectOpenSocket } from '@oneToOne/shared/contexts/useDisconnectOpenSocket'

/**
 * Customer Survey (NPS) for customers that have entered a conversation and have subsequently ended it.
 */
export const CustomerSurvey: FC = () => {
  const { sendUIStateTransitionEvent, hideWidget, setConversationSuveyClosed } =
    useStore(actionsSelector)
  const params = useStore(paramsSelector)
  const trackCustomerSurveyRating = useTrackSubmitCustomerSurveyRating()
  const trackCustomerSurveyFeedback = useTrackSubmitCustomerSurveyFeedback()

  const disconnectOpenSocket = useDisconnectOpenSocket()
  const { appointment } = useAppointment()

  // The conversation is cleared when the user confirms to end the conversation
  // thus we need to pass the previous conversation to the tracking event
  const previousConversation = useStore(previousConversationSelector)

  const initialPreviousConversationRef = useRef(previousConversation)

  // Set the initial value when the component first renders. This is needed
  // as agents can start new conversations with a customer while on this page
  // which will break the contract of the tracking event, as the previous
  // conversation will be null or incorrect.
  if (!initialPreviousConversationRef.current) {
    initialPreviousConversationRef.current = previousConversation
  }

  const closeCustomerSurvey = useCallback(
    (e?: SyntheticEvent) => {
      e?.stopPropagation()
      setConversationSuveyClosed()
      sendUIStateTransitionEvent({
        type: 'CLOSE_NPS',
        isAppointment: !!appointment,
      })
      if (params.dismiss_after_conversation_ended) {
        hideWidget()
        disconnectOpenSocket()
      }
    },
    [
      setConversationSuveyClosed,
      sendUIStateTransitionEvent,
      params.dismiss_after_conversation_ended,
      hideWidget,
      disconnectOpenSocket,
      appointment,
    ],
  )

  function handleSubmit(rating: number, feedback?: string) {
    if (feedback) {
      trackCustomerSurveyFeedback({
        rating,
        feedback,
        _conversation_id: initialPreviousConversationRef.current?.id,
      })
    } else {
      trackCustomerSurveyRating({
        rating,
        _conversation_id: initialPreviousConversationRef.current?.id,
      })
    }
  }

  return (
    <StatelessCustomerSurvey
      avatarUrl={initialPreviousConversationRef.current?.agent?.avatar_url}
      fallbackAvatarUrl={AGENT_FALLBACK_THUMBNAIL}
      onSubmit={handleSubmit}
      onClose={closeCustomerSurvey}
    />
  )
}
